import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  DialogContentText,
} from "@mui/material";
import { ButtonSpinner } from "../ui/ButtonSpinner";
import axios from "axios";

/* =========================================
   File Viewer Dialog
========================================= */
const FileViewerDialog = ({ open, onClose, fileUrl, fileType }) => {
  const isPdf = fileType?.includes("pdf");
  const isImage = fileType?.startsWith("image/");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5" textAlign="center" fontWeight="bold">
          File Viewer
        </Typography>
      </DialogTitle>

      <DialogContent>
        {fileUrl ? (
          <Box
            sx={{
              height: "75vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px solid #ddd",
              borderRadius: "10px",
              backgroundColor: "#f4f6f8",
              overflow: "hidden",
            }}
          >
            {isPdf && (
              <iframe
                src={fileUrl}
                title="PDF Viewer"
                width="100%"
                height="100%"
                style={{ border: "none" }}
              />
            )}

            {isImage && (
              <img
                src={fileUrl}
                alt="Uploaded File"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            )}

            {!isPdf && !isImage && (
              <Typography color="error">
                Unsupported file type
              </Typography>
            )}
          </Box>
        ) : (
          <Typography align="center">
            Unable to load the file.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* =========================================
   Main Dialog
========================================= */
const ViewPdfDialog = ({ open, onClose, courseId, source }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    return `Basic ${btoa(`${username}:${password}`)}`;
  };

  /* =========================================
     Detect File Type From URL
  ========================================= */
  const detectTypeFromUrl = (url) => {
    const lowerUrl = url.toLowerCase();

    if (lowerUrl.endsWith(".pdf")) return "application/pdf";

    if (
      lowerUrl.endsWith(".png") ||
      lowerUrl.endsWith(".jpg") ||
      lowerUrl.endsWith(".jpeg") ||
      lowerUrl.endsWith(".webp") ||
      lowerUrl.endsWith(".gif") ||
      lowerUrl.endsWith(".svg")
    )
      return "image/*";

    return "application/octet-stream";
  };

  /* =========================================
     Fetch Files List
  ========================================= */
  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${apiUrl}/api/pdf-urls/${courseId}/${source}`,
        {
          headers: {
            Authorization: getBasicAuthHeader(),
          },
        }
      );

      setFiles(response.data);
      //console.log(response.data)
    } catch (err) {
      console.error("Error fetching files:", err);
      setError("Failed to load files.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && courseId) {
      fetchFiles();
    }
  }, [open, courseId, source]);

  /* =========================================
     Download & Display File
  ========================================= */
  const downloadAndDisplayFile = async (url) => {
  try {
    setLoading(true);

    const apiUrl = process.env.REACT_APP_API_URL;

    const response = await axios.get(`${apiUrl}/api/download-pdf`, {
      params: { pdfUrl: url },
      headers: {
        Authorization: getBasicAuthHeader(),
      },
      responseType: "blob",
    });

    let contentType = response.headers["content-type"];

    const lowerUrl = url.toLowerCase();

    // 🔥 Force correct type for PDF
    if (lowerUrl.endsWith(".pdf")) {
      contentType = "application/pdf";
    }

    // 🔥 Force correct type for images
    else if (
      lowerUrl.endsWith(".png") ||
      lowerUrl.endsWith(".jpg") ||
      lowerUrl.endsWith(".jpeg") ||
      lowerUrl.endsWith(".webp") ||
      lowerUrl.endsWith(".gif") ||
      lowerUrl.endsWith(".svg")
    ) {
      contentType = "image/*";
    }

    const blob = new Blob([response.data], {
      type: contentType || "application/octet-stream",
    });

    const fileBlobUrl = URL.createObjectURL(blob);
    
    setSelectedFileUrl(fileBlobUrl);
    setSelectedFileType(contentType);
    setViewerOpen(true);
  } catch (err) {
    console.error("Error downloading file:", err);
    setError("Failed to load file.");
  } finally {
    setLoading(false);
  }
};

  const handleDeleteAttachment = async () => {
    if (!fileToDelete) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.delete(`${apiUrl}/api/deletePdf/${fileToDelete.pdfId}`, {
        headers: {
          Authorization: getBasicAuthHeader(),
        },
      });

      if (response.status === 200) {
        setConfirmOpen(false);
        setFileToDelete(null);
        await fetchFiles();
      } else {
        setError("Failed to delete attachment.");
      }
    } catch (err) {
      console.error("Error deleting attachment:", err);
      setError("Failed to delete attachment.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================
     Cleanup Blob URL
  ========================================= */
  useEffect(() => {
    return () => {
      if (selectedFileUrl) {
        URL.revokeObjectURL(selectedFileUrl);
      }
    };
  }, [selectedFileUrl]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h5" textAlign="center" fontWeight="bold">
            View Course Files
          </Typography>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" align="center">
              {error}
            </Typography>
          ) : (
            <Grid container spacing={2} mt={1}>
              {files.map((file, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Card
                    sx={{
                      position: "relative",
                      cursor: "pointer",
                      textAlign: "center",
                      borderRadius: "12px",
                      boxShadow: 3,
                      "&:hover": {
                        boxShadow: 6,
                        transform: "translateY(-3px)",
                        transition: "0.3s",
                      },
                    }}
                    onClick={() =>
                      downloadAndDisplayFile(file.pdfUrl)
                    }
                  >
                    <Typography
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFileToDelete(file);
                        setConfirmOpen(true);
                      }}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 1,
                        color: "error.main",
                        fontWeight: 900,
                        cursor: "pointer",
                        fontSize: "1.1rem",
                        lineHeight: 1,
                        transition: "all 0.2s ease",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255, 255, 255, 0.9)",
                        borderRadius: "50%",
                        boxShadow: 1,
                        "&:hover": {
                          transform: "scale(1.15)",
                          background: "#fff",
                          boxShadow: 2,
                        },
                      }}
                    >
                      X
                    </Typography>
                    <CardContent>
                      <Typography fontWeight="600">
                        {file.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, textAlign: "center" }}>
          ⚠️ Confirm Delete
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ textAlign: "center", mb: fileToDelete?.name ? 2 : 0 }}>
            Are you sure you want to delete this attachment?
          </DialogContentText>
          {fileToDelete?.name && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: "action.hover",
                borderLeft: "4px solid",
                borderColor: "primary.main",
                textAlign: "center",
              }}
            >
              <Typography fontWeight={700} color="text.primary" fontSize="0.95rem">
                {fileToDelete.name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAttachment}
            disabled={loading}
            variant="contained"
            color="error"
            endIcon={loading ? <ButtonSpinner size={18} /> : null}
          >
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <FileViewerDialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={selectedFileUrl}
        fileType={selectedFileType}
      />
    </>
  );
};

export default ViewPdfDialog;